/**
 * Acceptance marks for the card schemes the pay page takes. Each sits on a
 * white badge, as the schemes' guidelines ask, whatever the page background.
 */

const badgeClass =
  "h-11 w-[4.5rem] shrink-0 rounded-lg border border-background-300/70 bg-white flex items-center justify-center";

const brandAsset = (file: string) => `${import.meta.env.BASE_URL}brands/${file}`;

export function VisaLogo() {
  // Wordmark path from Simple Icons (CC0), in Visa blue.
  return (
    <span className={badgeClass} role="img" aria-label="Visa">
      <svg viewBox="0 8 24 8" className="h-5 w-auto" aria-hidden="true">
        <path
          fill="#1A1F71"
          d="M9.112 8.262L5.97 15.758H3.92L2.374 9.775c-.094-.368-.175-.503-.461-.658C1.447 8.864.677 8.627 0 8.479l.046-.217h3.3a.904.904 0 01.894.764l.817 4.338 2.018-5.102zm8.033 5.049c.008-1.979-2.736-2.088-2.717-2.972.006-.269.262-.555.822-.628a3.66 3.66 0 011.913.336l.34-1.59a5.207 5.207 0 00-1.814-.333c-1.917 0-3.266 1.02-3.278 2.479-.012 1.079.963 1.68 1.698 2.04.756.367 1.01.603 1.006.931-.005.504-.602.725-1.16.734-.975.015-1.54-.263-1.992-.473l-.351 1.642c.453.208 1.289.39 2.156.398 2.037 0 3.37-1.006 3.377-2.564m5.061 2.447H24l-1.565-7.496h-1.656a.883.883 0 00-.826.55l-2.909 6.946h2.036l.405-1.12h2.488zm-2.163-2.656l1.02-2.815.588 2.815zm-8.16-4.84l-1.603 7.496H8.34l1.605-7.496z"
        />
      </svg>
    </span>
  );
}

export function MastercardLogo() {
  // Mastercard's optimised web artwork, used unaltered via <img> as their web
  // quick-reference guide (2021.01.12) instructs. Its canvas carries the
  // required clear space; 56px wide sits in the guide's 45–73px range.
  return (
    <span className={badgeClass}>
      <img src={brandAsset("mc_symbol.svg")} alt="Mastercard" width={56} height={40} className="h-10 w-14" />
    </span>
  );
}


/**
 * Visa's own badge artwork (Visa Secure Brand Standards, Sept 2025), shown
 * unframed and unaltered as the standards require. A dark background needs
 * the variant with the white keyline.
 */
function VisaSecureBadge({ onDark }: { onDark: boolean }) {
  return (
    <img
      src={brandAsset(onDark ? "visa-secure-dark.png" : "visa-secure-light.png")}
      alt="Visa Secure"
      className="h-11 w-11 shrink-0"
    />
  );
}

// Hidden until Mastercard's official Identity Check artwork is available: the
// file below is a placeholder. Replace public/brands/mastercard-id-check.svg,
// then uncomment this and its use in CardBrands.
// function MastercardIdCheckMark() {
//   return (
//     <img
//       src={brandAsset("mastercard-id-check.svg")}
//       alt="Mastercard Identity Check"
//       className="h-11 w-auto shrink-0 rounded-lg border border-background-300/70"
//     />
//   );
// }

/** Not the PCI SSC logo, which only the Council's listed entities may use. */
export function PciDssBadge() {
  return (
    <span
      className="h-11 shrink-0 rounded-lg border border-background-300/70 bg-white px-2.5 flex items-center gap-1.5 text-[#141413]"
      role="img"
      aria-label="PCI DSS compliant payment environment"
    >
      <i className="ri-shield-check-fill text-xl text-[#0F7B3F]" aria-hidden="true"></i>
      <span className="flex flex-col leading-none">
        <span className="text-xs font-bold tracking-wide">PCI DSS</span>
        <span className="mt-0.5 text-[9px] font-medium uppercase tracking-wider text-[#5F5F5F]">Compliant</span>
      </span>
    </span>
  );
}

interface CardBrandsProps {
  className?: string;
  /** Add the Visa Secure and Mastercard Identity Check marks. */
  secure?: boolean;
  /** The marks sit on a dark background (the footer). */
  onDark?: boolean;
  /** Add the PCI DSS badge — on pages that take a payment. */
  pci?: boolean;
}

export default function CardBrands({ className = "", secure = false, onDark = false, pci = false }: CardBrandsProps) {
  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <VisaLogo />
      <MastercardLogo />
      {secure && (
        <>
          <VisaSecureBadge onDark={onDark} />
          {/* <MastercardIdCheckMark /> */}
        </>
      )}
      {pci && <PciDssBadge />}
    </div>
  );
}
